import { useState } from 'react';

import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import { Switch } from 'cpool-components/input/Switch';
import type { APPLICANT_ATTRIBUTION } from 'cpool-constants';
import { APPLICANT_ATTRIBUTIONS, MAIL_CONTENT_KINDS } from 'cpool-constants';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { useUserData } from '~/components/Header/menu/hooks/useUserData';
import type { InputKind } from '~/features/headerFooterTemplate/types';
import useDemandedDocuments from '~/features/leadDetails/hooks/useDemandedDocuments';
import { useDemandedDocumentsPreview } from '~/features/leadDetails/hooks/useDemandedDocumentsPreview';
import useLead from '~/features/leadDetails/hooks/useLead';
import useUserInfo from '~/hooks/useUserInfo';

type Props = {
    leadId: string;
};

export const MailPreview: React.FC<Props> = ({ leadId }) => {
    const { t } = useTranslation();
    const { data: lead } = useLead(leadId);
    const user = useUserInfo();
    const { data: userData } = useUserData(user?.id);
    const [contentKind, setContentKind] = useState<InputKind>(MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT);

    const { attribution, demandedDocuments, setAttribution } = useDemandedDocuments();
    const { isError, preview } = useDemandedDocumentsPreview({
        attribution,
        contentKind,
        demandedDocuments,
        lead,
        user: userData,
    });

    const applicant2DidNotAgreeToDataProtection = lead?.antragsteller2?.dataProtectionAgreed === false;
    const isHtml = contentKind === MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT;

    return (
        <div className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h6 className="text-sm font-medium text-midnight">{t('DOCUMENT_DEMANDER.PREVIEW.TITLE')}</h6>
                <div className="flex items-center gap-4">
                    {lead?.antragsteller2 && (
                        <DropdownMenu
                            disabled={applicant2DidNotAgreeToDataProtection}
                            onChange={(value) => setAttribution(value as APPLICANT_ATTRIBUTION)}
                            options={Object.values(APPLICANT_ATTRIBUTIONS).map((value) => ({
                                label: t(`APPLICANTS.${value}`),
                                value,
                            }))}
                            value={attribution}
                        />
                    )}
                    {/* Switch renders its own label first, so the caption is separate */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-midnight">{t('MESSAGE.EMAIL.HTML_CONTENT')}</span>
                        <Switch
                            id="demand-mail-html-preview"
                            aria-label={t('MESSAGE.EMAIL.HTML_CONTENT')}
                            checked={isHtml}
                            onChange={(event) =>
                                setContentKind(
                                    event.target.checked
                                        ? MAIL_CONTENT_KINDS.MAIL_HTML_CONTENT
                                        : MAIL_CONTENT_KINDS.MAIL_CONTENT,
                                )
                            }
                        />
                    </div>
                </div>
            </div>
            <div className="p-4 overflow-y-auto bg-white border rounded-md max-h-96 border-grey-outline-light">
                {isError && (
                    <p role="alert" className="text-sm text-grey">
                        {t('TEMPLATES.INFO_MESSAGES.LOADING_TEMPLATES_FAILED')}
                    </p>
                )}
                {!isError && isHtml && (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(preview?.join('') as string, {
                                ADD_TAGS: ['style'],
                                FORCE_BODY: true,
                            }),
                        }}
                    />
                )}
                {!isError && !isHtml && preview}
            </div>
        </div>
    );
};
